import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { leaderboardConfigApi } from '../api';
import type {
  CreateLeaderboardPeriodPayload,
  LeaderboardDefinition,
  LeaderboardDefinitionId,
  LeaderboardPeriod,
  LeaderboardTierList,
  ReplaceLeaderboardTiersPayload,
  SettlementSummary,
  UpdateLeaderboardPeriodPayload,
} from '../types';
import { getDefaultDefinitions, getErrorMessage } from '../utils';

type State = {
  definitions: LeaderboardDefinition[];
  tierList: LeaderboardTierList | null;
  periods: LeaderboardPeriod[];
  settlement: SettlementSummary | null;
};

export function useLeaderboardConfig(
  activeId: LeaderboardDefinitionId,
  rewardPeriodId: string,
  settlementPeriodId: string
) {
  const mountedRef = useRef(true);
  const definitionsAbortRef = useRef<AbortController | null>(null);
  const tiersAbortRef = useRef<AbortController | null>(null);

  const [data, setData] = useState<State>({
    definitions: getDefaultDefinitions(),
    tierList: null,
    periods: [],
    settlement: null,
  });

  const [loading, setLoading] = useState(false);
  const [tierLoading, setTierLoading] = useState(false);
  const [periodsLoading, setPeriodsLoading] = useState(false);
  const [settlementLoading, setSettlementLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const periodsAbortRef = useRef<AbortController | null>(null);
  const settlementAbortRef = useRef<AbortController | null>(null);

  const fetchDefinitions = useCallback(async () => {
    definitionsAbortRef.current?.abort();

    const controller = new AbortController();
    definitionsAbortRef.current = controller;

    setLoading(true);
    setError(null);

    try {
      const definitions = await leaderboardConfigApi.getDefinitions(controller.signal);

      if (!mountedRef.current) return;

      setData((prev) => ({
        ...prev,
        definitions,
      }));
    } catch (err) {
      if (mountedRef.current) {
        setError(new Error(getErrorMessage(err)));
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, []);

  const fetchTiers = useCallback(async (periodId: string, id: LeaderboardDefinitionId) => {
    if (!periodId || !id) {
      setData((prev) => ({ ...prev, tierList: null }));
      return;
    }

    tiersAbortRef.current?.abort();

    const controller = new AbortController();
    tiersAbortRef.current = controller;

    setTierLoading(true);
    setError(null);

    try {
      const tierList = await leaderboardConfigApi.getTiers(periodId, id, controller.signal);

      if (!mountedRef.current) return;

      setData((prev) => ({
        ...prev,
        tierList,
      }));
    } catch (err) {
      if (mountedRef.current) {
        setError(new Error(getErrorMessage(err)));
      }
    } finally {
      if (mountedRef.current) {
        setTierLoading(false);
      }
    }
  }, []);

  const replaceTiers = useCallback(
    async (
      periodId: string,
      id: LeaderboardDefinitionId,
      payload: ReplaceLeaderboardTiersPayload
    ) => {
      setSaving(true);

      try {
        return await leaderboardConfigApi.replaceTiers(periodId, id, payload);
      } finally {
        if (mountedRef.current) {
          setSaving(false);
        }
      }
    },
    []
  );

  const copyTiersFrom = useCallback(
    async (periodId: string, id: LeaderboardDefinitionId, fromPeriodId: string) => {
      setSaving(true);
      try {
        return await leaderboardConfigApi.copyTiersFrom(periodId, id, fromPeriodId);
      } finally {
        if (mountedRef.current) setSaving(false);
      }
    },
    []
  );

  const applyTiersToAll = useCallback(
    async (periodId: string, id: LeaderboardDefinitionId) => {
      setSaving(true);
      try {
        return await leaderboardConfigApi.applyTiersToAll(periodId, id);
      } finally {
        if (mountedRef.current) setSaving(false);
      }
    },
    []
  );

  const fetchPeriods = useCallback(async () => {
    periodsAbortRef.current?.abort();

    const controller = new AbortController();
    periodsAbortRef.current = controller;

    setPeriodsLoading(true);

    try {
      const periods = await leaderboardConfigApi.listPeriods(controller.signal);
      if (!mountedRef.current) return;
      setData((prev) => ({ ...prev, periods }));
    } catch (err) {
      if (mountedRef.current) {
        setError(new Error(getErrorMessage(err)));
      }
    } finally {
      if (mountedRef.current) {
        setPeriodsLoading(false);
      }
    }
  }, []);

  const createPeriod = useCallback(async (payload: CreateLeaderboardPeriodPayload) => {
    setSaving(true);
    try {
      return await leaderboardConfigApi.createPeriod(payload);
    } finally {
      if (mountedRef.current) setSaving(false);
    }
  }, []);

  const updatePeriod = useCallback(
    async (periodId: string, payload: UpdateLeaderboardPeriodPayload) => {
      setSaving(true);
      try {
        return await leaderboardConfigApi.updatePeriod(periodId, payload);
      } finally {
        if (mountedRef.current) setSaving(false);
      }
    },
    []
  );

  const deletePeriod = useCallback(async (periodId: string) => {
    setSaving(true);
    try {
      return await leaderboardConfigApi.deletePeriod(periodId);
    } finally {
      if (mountedRef.current) setSaving(false);
    }
  }, []);

  const fetchSettlement = useCallback(
    async (periodId: string, id: LeaderboardDefinitionId) => {
      if (!periodId || !id) {
        setData((prev) => ({ ...prev, settlement: null }));
        return;
      }

      settlementAbortRef.current?.abort();
      const controller = new AbortController();
      settlementAbortRef.current = controller;

      setSettlementLoading(true);
      try {
        const settlement = await leaderboardConfigApi.getSettlement(
          periodId,
          id,
          controller.signal
        );
        if (!mountedRef.current) return;
        setData((prev) => ({ ...prev, settlement }));
      } catch (err) {
        if (mountedRef.current) {
          setError(new Error(getErrorMessage(err)));
        }
      } finally {
        if (mountedRef.current) setSettlementLoading(false);
      }
    },
    []
  );

  const payAllSettlement = useCallback(
    async (periodId: string, id: LeaderboardDefinitionId) => {
      setSaving(true);
      try {
        return await leaderboardConfigApi.payAllSettlement(periodId, id);
      } finally {
        if (mountedRef.current) setSaving(false);
      }
    },
    []
  );

  const payEntry = useCallback(async (entryId: string) => {
    setSaving(true);
    try {
      return await leaderboardConfigApi.payEntry(entryId);
    } finally {
      if (mountedRef.current) setSaving(false);
    }
  }, []);

  const retryEntry = useCallback(async (entryId: string) => {
    setSaving(true);
    try {
      return await leaderboardConfigApi.retryEntry(entryId);
    } finally {
      if (mountedRef.current) setSaving(false);
    }
  }, []);

  // Amount override requests (XBIT-4530). These create or decide a request; they change nothing upstream
  // until a different admin approves, so they do not touch `saving`.
  const requestAmountOverride = useCallback(
    (entryId: string, body: { amountUsdc: string; reason?: string }) =>
      leaderboardConfigApi.requestAmountOverride(entryId, body),
    []
  );
  const listAmountRequests = useCallback(
    (params?: { page?: number; size?: number; status?: string }) =>
      leaderboardConfigApi.listAmountRequests(params),
    []
  );
  const approveAmountRequest = useCallback(
    (id: string) => leaderboardConfigApi.approveAmountRequest(id),
    []
  );
  const rejectAmountRequest = useCallback(
    (id: string, reason: string) => leaderboardConfigApi.rejectAmountRequest(id, reason),
    []
  );
  const cancelAmountRequest = useCallback(
    (id: string) => leaderboardConfigApi.cancelAmountRequest(id),
    []
  );

  const exportRanking = useCallback(async (periodId: string) => {
    await leaderboardConfigApi.exportRanking(periodId);
  }, []);

  useEffect(() => {
    mountedRef.current = true;

    fetchDefinitions();
    fetchPeriods();

    return () => {
      mountedRef.current = false;
      definitionsAbortRef.current?.abort();
      tiersAbortRef.current?.abort();
      periodsAbortRef.current?.abort();
      settlementAbortRef.current?.abort();
    };
  }, [fetchDefinitions, fetchPeriods]);

  useEffect(() => {
    fetchTiers(rewardPeriodId, activeId);
  }, [rewardPeriodId, activeId, fetchTiers]);

  useEffect(() => {
    fetchSettlement(settlementPeriodId, activeId);
  }, [settlementPeriodId, activeId, fetchSettlement]);

  return useMemo(
    () => ({
      ...data,
      loading,
      tierLoading,
      periodsLoading,
      settlementLoading,
      saving,
      error,
      refetchDefinitions: fetchDefinitions,
      refetchTiers: () => fetchTiers(rewardPeriodId, activeId),
      refetchPeriods: fetchPeriods,
      refetchSettlement: () => fetchSettlement(settlementPeriodId, activeId),
      replaceTiers,
      copyTiersFrom,
      applyTiersToAll,
      createPeriod,
      updatePeriod,
      deletePeriod,
      payAllSettlement,
      payEntry,
      retryEntry,
      requestAmountOverride,
      listAmountRequests,
      approveAmountRequest,
      rejectAmountRequest,
      cancelAmountRequest,
      exportRanking,
    }),
    [
      activeId,
      rewardPeriodId,
      settlementPeriodId,
      applyTiersToAll,
      copyTiersFrom,
      createPeriod,
      data,
      deletePeriod,
      error,
      fetchDefinitions,
      fetchPeriods,
      fetchSettlement,
      fetchTiers,
      loading,
      payAllSettlement,
      payEntry,
      periodsLoading,
      replaceTiers,
      retryEntry,
      requestAmountOverride,
      listAmountRequests,
      approveAmountRequest,
      rejectAmountRequest,
      cancelAmountRequest,
      exportRanking,
      saving,
      settlementLoading,
      tierLoading,
      updatePeriod,
    ]
  );
}
